import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import React from "react";
import styles from "./TestimonialsSection.module.css";

/* ILLUSTRATIVE personas, not real reviewers (item 25). These quotes are
   written by the team to show typical conversations and are LABELLED as such
   by the section's default title and subtitle below. They must never claim an
   outcome or an accuracy ("it actually happened") - that is a testimonial
   claim, and these are not testimonials. Real, verbatim user feedback lives in
   internationalTestimonials.ts (supplied by the founder on 2026-10-02) and is
   what the international pages pass in; swap this set for a real Indian set
   the same way when one is supplied. No ages or occupations: inventing a job
   for a persona adds false specificity the label cannot cure. */
const testimonialsData = [
  {
    quote:
      "I asked about a job change and the answer walked through my own chart: the houses involved, my current mahadasha and the timing windows. It helped me structure the decision.",
    author: "Priya",
    photo: "/ugc/avatar-priya.jpg",
    accent: "linear-gradient(135deg, #ff2e93, #ff6b6b)",
  },
  {
    quote:
      "The reading explained which mahadasha I am running and what it usually brings, in plain language. I could see why it said what it said.",
    author: "Arun",
    photo: "/ugc/avatar-arun.jpg",
    accent: "linear-gradient(135deg, #8c42ff, #ff2e93)",
  },
  {
    quote:
      "I used it late at night for a relationship question. The compatibility breakdown was detailed and easy to follow.",
    author: "Meera",
    photo: "/ugc/avatar-meera.jpg",
    accent: "linear-gradient(135deg, #ff8c42, #ff2e93)",
  },
  {
    quote:
      "I started with the free question, then explored my full kundli. Seeing the houses and dashas laid out made it feel practical instead of mystical.",
    author: "Ravi",
    photo: "/ugc/avatar-ravi.jpg",
    accent: "linear-gradient(135deg, #2e8cff, #8c42ff)",
  },
  {
    quote:
      "I check it before big weeks at work. It gives me a structured way to reflect, grounded in my chart instead of a generic horoscope line.",
    author: "Shalini",
    photo: "/ugc/avatar-shalini.jpg",
    accent: "linear-gradient(135deg, #ff5e2e, #ffc542)",
  },
];

interface TestimonialsSectionProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Force the illustrative label when passing staged (non-review) items. */
  illustrative?: boolean;
  microcopy?: React.ReactNode;
  /** Overrides the cards. The default set is written for India (Priya M.,
      Arun S. ...), which is the wrong social proof on /usa and /uae, so those
      pages pass internationalTestimonials instead. photo and details are
      OPTIONAL: the international set has neither, and inventing them would be
      inventing an appearance and a job for a real person. */
  items?: Array<{
    quote: string;
    author: string;
    accent: string;
    photo?: string;
    details?: string;
  }>;
}

export default function TestimonialsSection({
  title,
  subtitle,
  microcopy,
  items,
  illustrative,
}: TestimonialsSectionProps = {}) {
  const cards = items ?? testimonialsData;
  // No items passed means the illustrative default set: the heading and
  // subtitle must then LABEL it instead of claiming real experiences. Pages
  // passing staged items (the international set on /love /horoscope) force
  // the label with the prop, the names there are not real reviews either.
  const isIllustrative = illustrative ?? !items;
  const carouselRef = React.useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = React.useState(false);
  const [isInteracting, setIsInteracting] = React.useState(false);

  const scroll = (direction: "left" | "right") => {
    if (carouselRef.current) {
      const scrollAmount = direction === "left" ? -400 : 400;
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  // Auto-scroll effect
  React.useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    let intervalId: NodeJS.Timeout;

    const autoScroll = () => {
      if (!isInteracting) {
        carousel.scrollLeft += 1;

        // Reset to beginning when reaching halfway (for seamless loop)
        const halfWidth = carousel.scrollWidth / 2;
        if (carousel.scrollLeft >= halfWidth) {
          carousel.scrollLeft = 0;
        }
      }
    };

    // Start the animation with setInterval for more reliable execution
    intervalId = setInterval(autoScroll, 20); // 20ms = ~50fps

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isInteracting]);

  return (
    <section className={styles.testimonialsSection}>
      {/* Top Gradient Fade */}
      <div className={styles.topFade} />

      <div className={styles.header}>
        <h2 className={styles.title}>
          {title ??
            (isIllustrative ? "How People Use Aura AI" : "What Our Users Say")}
        </h2>
        <p className={styles.subtitle}>
          {subtitle ??
            (isIllustrative
              ? "Illustrative examples written by the Aura AI team to show typical conversations. Not user reviews."
              : "Real experiences from Aura AI users around the world")}
        </p>
      </div>

      <div className={styles.carouselWrapper}>
        <div
          className={styles.testimonialsCarousel}
          ref={carouselRef}
          onMouseDown={() => setIsInteracting(true)}
          onMouseUp={() => setIsInteracting(false)}
          onTouchStart={() => setIsInteracting(true)}
          onTouchEnd={() => setIsInteracting(false)}
        >
          {/* Duplicate testimonials for infinite scroll effect */}
          {[...cards, ...cards].map((t, index) => (
            <motion.div
              key={index}
              className={styles.testimonialCard}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: (index % cards.length) * 0.1,
              }}
            >
              <header className={styles.authorInfo}>
                {/* AI-generated portraits (Gemini), not photographs of real
                    people: using a real person's likeness to endorse the app
                    would be misappropriation. Source PNGs were discarded; only
                    the 3KB crops ship. */}
                <span
                  className={styles.avatar}
                  style={{ background: t.accent }}
                  aria-hidden="true"
                >
                  {/* Accent gradient stays underneath as the fallback, so a
                      failed image leaves a coloured disc rather than a hole. */}
                  {t.photo ? (
                    <img
                      className={styles.avatarImg}
                      src={t.photo}
                      alt=""
                      width={88}
                      height={88}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    /* No portrait supplied for this person. Showing a stock or
                       generated face under their real name would invent an
                       appearance for them, so the initial stands in. */
                    <span className={styles.avatarInitial}>
                      {t.author.charAt(0)}
                    </span>
                  )}
                </span>
                <span className={styles.authorText}>
                  <span className={styles.authorName}>{t.author}</span>
                  {t.details && (
                    <span className={styles.authorDetails}>{t.details}</span>
                  )}
                </span>
              </header>
              <Quote className={styles.quoteIcon} />
              <blockquote className={styles.quote}>"{t.quote}"</blockquote>
            </motion.div>
          ))}
        </div>
      </div>

      {microcopy && <p className={styles.microcopy}>{microcopy}</p>}

      {/* Bottom Gradient Fade */}
      <div className={styles.bottomFade} />
    </section>
  );
}
